<section>
    <div class="bg-white p-6">
        <h1 class="typography-title-m m-0">
            {{'create-claim.documentation.title' | translate}}
        </h1>
        <div class="divider mt-3 mb-6"></div>

<!--        Initial questions-->
        <div *ngIf="requiredDocuments.checks.length > 0">
            <h3 class="m-0 typography-body-l-medium">
                {{'create-claim.documentation.answer_these_questions' | translate}}:
            </h3>
            <div class="flex flex-col gap-3 mt-4">
                <ng-container *ngFor="let question of requiredDocuments.checks; let i = index">
                    <ng-container *ngIf="shouldShowQuestion(question, i)">
                        <div class="flex flex-wrap gap-4">
                            <div class="flex flex-col gap-4 px-4">
                                <p class="m-0 typography-body-m-regular">
                                    {{question.label}}
                                </p>
                                <div class="flex gap-6">
                                    <div class="flex gap-2 items-center cursor-pointer" (click)="checkQuestion(i)">
                                        <app-radio [checked]="question.isChecked === true || false" />
                                        <span class="typography-body-m-medium">{{'yes' | translate}}</span>
                                    </div>
                                    <div class="flex gap-2 items-center cursor-pointer" (click)="uncheckQuestion(i)">
                                        <app-radio [checked]="question.isChecked === false || false" />
                                        <span class="typography-body-m-medium">{{'no' | translate}}</span>
                                    </div>
                                </div>
                            </div>
                            <div *ngIf="question.additionalInfoLabel && question.isChecked" class="flex flex-col gap-2 px-4">
                                <p class="m-0 typography-body-m-medium">
                                    {{question.additionalInfoLabel}}
                                    <span *ngIf="!question.additionalInfoIsRequired">&nbsp;({{'create-claim.optional' | translate}})</span>
                                </p>
                                <input
                                    type="text"
                                    class="input flex-1 py-2 px-4 typography-body-m-regular border border-border-secondary"
                                    [(ngModel)]="question.additionalInfoValue" (ngModelChange)="onClaimDocumentationChange()"
                                />
                            </div>
                        </div>
                    </ng-container>
                </ng-container>
            </div>
        </div>

<!--        Add documents-->
        <div *ngIf="areQuestionsComplete()" [ngClass]="{'mt-8': requiredDocuments.checks.length > 0, 'mb-8': requiredDocuments.additionalInfo.length > 0}">
            <h3 class="m-0 typography-body-l-medium">
                {{'create-claim.documentation.attach_the_following_documents' | translate}}:
            </h3>
            <p class="mt-1 mb-6 typography-body-s-regular text-text-tertiary">
                {{'create-claim.documentation.documents_available_formats' | translate: {varSize: 12} }}
            </p>
            <div class="grid grid-cols-1 md:grid-cols-[repeat(2,1fr)] xl:grid-cols-[repeat(3,1fr)] gap-6">
                <ng-container *ngFor="let document of requiredDocuments.documents; let i = index">
                    <div
                        *ngIf="shouldShowDocument(document)"
                        class="p-4 flex flex-col gap-6 border border-border-secondary border-dashed rounded-[10px] relative"
                        [ngClass]="{'border-semantic-success border-solid': document.document, 'border-border-secondary border-dashed': !document.document}"
                    >
                        <p class="m-0 typography-body-m-regular">
                            {{document.label}}{{document.isRequired ? '*' : ' (' + ('create-claim.optional' | translate) + ')'}}
                        </p>
                        <div *ngIf="document.additionalInfoLabel" class="mt-[-20px] flex-1">
                            <p class="m-0 typography-body-s-light">
                                {{document.additionalInfoLabel}}
                            </p>
                        </div>
                        <div *ngIf="document.extraDownloadableDocumentLabel" class="flex items-center mt-[-20px] cursor-pointer flex-1" (click)="downloadDocument(document.extraDownloadableDocumentLink)">
                            <img class="w-[24px] h-[24px]" src="assets/icons/download.svg" alt="Download">
                            <p class="m-0 typography-body-s-regular text-primary">
                                {{document.extraDownloadableDocumentLabel}}
                            </p>
                        </div>
                        <div *ngIf="!document.extraDownloadableDocumentLabel && !document.additionalInfoLabel" class="flex-1"></div>
                        <div class="w-full">
                            <app-button *ngIf="!document.document" [size]="'medium'" [block]="true" (onClick)="onOpenFileSelect(i)">{{'create-claim.upload_document' | translate}}</app-button>
                            <div *ngIf="document.document" class="flex flex-wrap gap-6">
                                <div class="flex gap-2">
                                    <img src="assets/icons/document2.svg" alt="doc">
                                    <p class="m-0 typography-body-m-regular">
                                        {{document.document.title}}
                                    </p>
                                </div>
                                <div class="flex gap-4">
                                    <p class="m-0 typography-body-s-regular text-text-secondary">
                                        {{formatBytes(document.document.size)}}
                                    </p>
                                    <img class="h-[18px] w-[18px] cursor-pointer" src="assets/icons/close.svg" alt="delete" (click)="onDeleteFile(document, i)">
                                </div>
                            </div>
                        </div>
                    </div>
                    <input #claimDocumentInput [id]="'claim_document__' + i" type="file" hidden [accept]="acceptedDocumentsString" (change)="onFileSelected($event, i)" />
                </ng-container>
            </div>
        </div>

<!--        Additional info text areas-->
        <div *ngIf="areQuestionsComplete() && requiredDocuments.additionalInfo.length > 0" class="flex flex-col gap-4">
            <ng-container *ngFor="let additionalInfo of requiredDocuments.additionalInfo">
                <div *ngIf="additionalInfo.shouldShow !== false">
                    <label [for]="additionalInfo.id" class="typography-body-m-medium">
                        {{additionalInfo.label}}{{additionalInfo.isRequired ? '*' : ''}}
                    </label>
                    <div class="mt-2 flex">
                        <textarea
                            [id]="additionalInfo.id"
                            class="p-4 typography-body-m-regular h-[96px] flex-1 border border-border-secondary outline-none"
                            [placeholder]="'create-claim.additional_info_placeholder' | translate"
                            [(ngModel)]="additionalInfo.value" (ngModelChange)="onClaimDocumentationChange()"
                        ></textarea>
                    </div>
                </div>
            </ng-container>
        </div>

<!--        Information for the client-->
        <div *ngIf="areQuestionsComplete()" class="mt-8">
            <p class="m-0 typography-body-s-regular text-text-tertiary">*{{'create-claim.mandatory_documents' | translate}}</p>
            <div class="bg-info-background p-2 mt-8 flex gap-2">
                <img class="w-[24px] h-[24px]" src="assets/icons/info.svg" alt="info">
                <div class="flex flex-col justify-center gap-1">
                    <p class="m-0 typography-body-m-regular">
                        {{'create-claim.classification.extra_documentation_might_be_required' | translate}}
                    </p>
                </div>
            </div>
        </div>
    </div>
</section>

<app-confirm-modal
    [isOpen]="isErrorModalVisible"
    title="{{ 'create-claim.documentation.error_uploading_file' | translate }}"
    message="{{errorMessage}}"
    confirmText="{{'common.buttons.close' | translate}}"
    cancelText=""
    (onConfirm)="isErrorModalVisible = false"
    (onCancel)="isErrorModalVisible = false"
    (onClose)="isErrorModalVisible = false"
/>
